import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/theme';
import { Calendar } from 'lucide-react';

// Type-first date field. The visitor types digits (e.g. 01022000) which are
// auto-formatted to DD/MM/YYYY; a calendar icon opens the native picker as an
// optional fallback. The component reads/emits the standard YYYY-MM-DD value the
// rest of the app stores, so saved data and reports are unaffected.

// Raw input (digits, possibly with slashes) -> DD/MM/YYYY display text.
function maskDisplay(raw) {
  const d = String(raw).replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// DD/MM/YYYY -> YYYY-MM-DD, but only if it is a real calendar date; else ''.
function displayToIso(display) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display);
  if (!m) return '';
  const dd = +m[1], mm = +m[2], yyyy = +m[3];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2100) return '';
  const dt = new Date(yyyy, mm - 1, dd);
  // Round-trip check rejects impossible dates like 31/02 or 30/02.
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

// Stored value (YYYY-MM-DD or full ISO) -> DD/MM/YYYY display text.
function isoToDisplay(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const DateInput = ({ label, value = '', onChange, error, required, id }) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const [text, setText] = useState(() => isoToDisplay(value));
  const lastEmitted = useRef(value || '');
  const nativeRef = useRef(null);

  // Refresh the display when the value changes for external reasons (e.g. an
  // existing client's saved date loading in), but never while the user is
  // mid-type (that would wipe their partial entry).
  useEffect(() => {
    if ((value || '') !== lastEmitted.current) {
      setText(isoToDisplay(value));
      lastEmitted.current = value || '';
    }
  }, [value]);

  const emit = (iso) => {
    lastEmitted.current = iso;
    if (onChange) onChange(iso);
  };

  const handleType = (e) => {
    const masked = maskDisplay(e.target.value);
    setText(masked);
    emit(displayToIso(masked));
  };

  const handleNative = (e) => {
    const iso = e.target.value; // YYYY-MM-DD from the native picker
    setText(isoToDisplay(iso));
    emit(iso || '');
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch (_) { /* fall through */ }
    }
    el.focus();
    el.click();
  };

  // Gentle inline hint when a fully-typed date isn't a real one.
  const localError = (!error && text.length === 10 && !displayToIso(text))
    ? 'Please enter a valid date (DD/MM/YYYY)'
    : '';
  const shownError = error || localError;
  const nativeValue = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : (displayToIso(text) || '');

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/YYYY"
          maxLength={10}
          value={text}
          onChange={handleType}
          className={cn(
            'w-full px-4 py-2 pr-11 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
            'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600',
            'text-gray-900 dark:text-white placeholder-gray-400',
            shownError && 'border-red-500 focus:ring-red-500'
          )}
        />
        {/* Calendar icon (visual only). */}
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 dark:text-gray-500">
          <Calendar className="w-5 h-5" />
        </span>
        {/* Transparent native date input over the icon: tapping/clicking opens the picker. */}
        <input
          ref={nativeRef}
          type="date"
          tabIndex={-1}
          aria-label={label ? `${label} calendar picker` : 'Open calendar picker'}
          value={nativeValue}
          onChange={handleNative}
          onClick={openPicker}
          className="absolute inset-y-0 right-0 w-11 opacity-0 cursor-pointer"
        />
      </div>
      {shownError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{shownError}</p>}
    </div>
  );
};

export default DateInput;
